import { Box, Typography, Tooltip } from '@mui/material'
import { FS_SM } from '../common/theme'
import { FolderIcon } from './FolderIcon'

interface FolderChipProps {
  name: string
  // Picks the folder colour: gold (project) when true, blue (knowledge base)
  // when false / omitted.
  isProject?: boolean
  // Click → typically navigates into the folder. When omitted the chip is a
  // plain, borderless label suitable for table rows.
  onClick?: () => void
  // Drop the border / background even when clickable. Hover tint stays.
  noBorder?: boolean
  // Render the name in bold. Defaults to true. Set false for inline /
  // table-row contexts where the surrounding cell already carries emphasis.
  bold?: boolean
  // Disable the folder-icon skew animation on hover. Use for purely static
  // labels (breadcrumbs, headers) where the chip is decorative and a hover
  // animation would be misleading.
  noHover?: boolean
  // Scale factor applied to the folder icon AND the label font size. 1 keeps
  // the chip at its default sizing; values < 1 produce compact variants
  // suitable for tight meta rows.
  scale?: number
  tooltip?: string
  maxWidth?: number
  testId?: string
}

// Folder + name chip. Generic over a folder name and a project/KB flag.
// Three visual modes:
//   • clickable + bordered (default)  → pill with border + hover state.
//   • clickable + noBorder            → no frame, hover tint only.
//   • read-only                       → no border / no background.
export default function FolderChip({
  name, isProject,
  onClick, noBorder,
  bold = false,
  noHover,
  scale = 1,
  tooltip = 'Open folder',
  maxWidth = 220,
  testId = 'folder_chip',
}: FolderChipProps) {
  const clickable = !!onClick
  // FolderIcon ships with a baked-in scale of 0.75 - multiply rather than
  // replace so callers' `scale` reads as a relative factor (1 = default).
  const iconScale = 0.75 * scale
  const fontSize = `${parseFloat(FS_SM) * scale}rem`

  const content = (
    <Box
      data-testid={testId}
      onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center',
        // Folder-icon skew animation on hover. Drives the `::after` overlay
        // baked into FolderIcon. Fires whenever the chip itself is hovered,
        // so it works both for stand-alone clickable chips and for chips
        // sitting inside a list-row click target. Skipped when `noHover` is
        // set, for purely decorative labels.
        ...(noHover ? {} : {
          '&:hover .kbFolderIcon::after': {
            opacity: 1,
            transform: 'skewX(-10deg)',
            marginLeft: '2px',
            marginTop: '3px',
            height: '17px',
          },
        }),
        ...(clickable
          ? (noBorder
            ? {
              px: 0.75, py: 0.4, borderRadius: 1,
              cursor: 'pointer',
              transition: 'background-color 0.12s',
              '&:hover': { bgcolor: 'action.hover' },
            }
            : {
              px: 0.75, py: 0.4, borderRadius: 1,
              cursor: 'pointer',
              bgcolor: 'action.hover',
              border: 1, borderColor: 'divider',
              transition: 'background-color 0.12s, border-color 0.12s',
              '&:hover': { bgcolor: 'action.selected', borderColor: 'primary.main' },
            })
          : {
            cursor: 'default',
          }),
      }}
    >
      <FolderIcon isProject={isProject} sx={{ scale: iconScale, mr: scale == 1 ? 0.5 : 0 }} />
      <Typography
        variant="caption"
        sx={{
          fontSize, fontWeight: bold ? 600 : 400,
          maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Typography>
    </Box>
  )

  return clickable ? <Tooltip title={tooltip} disableInteractive>{content}</Tooltip> : content
}
